import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles, categories } from '@/mocks/skincare';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: category?.name || '分类' },
  ];

  if (!category) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen flex items-center justify-center px-6">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-emotion-sad-line text-2xl"></i>
            </div>
            <h2 className="text-xl font-semibold text-foreground-950 mb-2">分类未找到</h2>
            <p className="text-sm text-foreground-500 mb-6">该分类不存在或已被移除</p>
            <Link to="/" className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm hover:bg-primary-600 transition-colors whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main>
        <div className="bg-background-100/70 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-8">
            <Breadcrumb items={breadcrumbItems} />
            <div className="flex items-center gap-4 mt-2">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                <i className={`${category.icon} text-xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-semibold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">{category.description}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/"
              className="px-3 py-1.5 rounded-full text-xs border border-background-300 text-foreground-600 hover:border-primary-300 hover:text-primary-500 transition-colors whitespace-nowrap"
            >
              全部分类
            </Link>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-3 py-1.5 rounded-full text-xs transition-colors whitespace-nowrap ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'border border-background-300 text-foreground-600 hover:border-primary-300 hover:text-primary-500'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>

        <section className="py-8 md:py-12 px-6 md:px-10">
          <div className="max-w-7xl mx-auto">
            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {categoryArticles.map((article) => (
                  <ArticleCard key={article.slug} {...article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                  <i className="ri-file-list-3-line text-2xl"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类下暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": `${category.name} - 裸肌研究所`,
        "description": category.description,
        "url": `https://jjchuang.cn/category/${slug}`,
        "inLanguage": "zh-CN",
        "isPartOf": { "@type": "WebSite", "name": "裸肌研究所", "url": "https://jjchuang.cn" }
      }) }}></script>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://jjchuang.cn" },
          { "@type": "ListItem", "position": 2, "name": category.name, "item": `https://jjchuang.cn/category/${slug}` }
        ]
      }) }}></script>
    </>
  );
}